import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  Search,
  ShoppingBag,
  Bot,
  Shield,
  Clock,
  TrendingUp,
  MessageSquare,
} from 'lucide-react';
import type { Deal } from '../utils/api';

interface DealsPageProps {
  deals: Deal[];
  onBack: () => void;
  onDealClick: (dealId: string) => void;
}

export function DealsPage({ deals, onBack, onDealClick }: DealsPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | Deal['status']>('all');

  const getStatusColor = (status: Deal['status']) => {
    switch (status) {
      case 'negotiating':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-400/30';
      case 'confirmed':
        return 'bg-blue-500/20 text-blue-300 border-blue-400/30';
      case 'held':
        return 'bg-purple-500/20 text-purple-300 border-purple-400/30';
      case 'completed':
        return 'bg-green-500/20 text-green-300 border-green-400/30';
      case 'disputed':
        return 'bg-red-500/20 text-red-300 border-red-400/30';
      case 'cancelled':
        return 'bg-white/10 text-white/60 border-white/20';
      default:
        return 'bg-white/10 text-white/60 border-white/20';
    }
  };

  const getStatusText = (status: Deal['status']) => {
    switch (status) {
      case 'negotiating':
        return 'กำลังเจรจา';
      case 'confirmed':
        return 'ยืนยันแล้ว';
      case 'held':
        return 'เงินมัดจำ';
      case 'completed':
        return 'สำเร็จ';
      case 'disputed':
        return 'ข้อพิพาท';
      case 'cancelled':
        return 'ยกเลิก';
      default:
        return status;
    }
  };

  const filteredDeals = deals.filter(deal => {
    const matchesSearch = deal.item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         deal.item.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || deal.status === filter;
    return matchesSearch && matchesFilter;
  });

  const statusCounts = {
    all: deals.length,
    negotiating: deals.filter(d => d.status === 'negotiating').length,
    confirmed: deals.filter(d => d.status === 'confirmed').length,
    completed: deals.filter(d => d.status === 'completed').length,
  };

  return (
    <div className="space-y-6 pb-24">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <button
          onClick={onBack}
          className="p-2 rounded-xl glass hover:bg-white/15 transition-all active:scale-95"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        <h1 className="text-2xl font-bold text-white">ดีลของฉัน</h1>
        <div className="w-10" />
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-4 gap-2"
      >
        <div className="glass rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-white">{statusCounts.all}</p>
          <p className="text-xs text-white/60">ทั้งหมด</p>
        </div>
        <div className="glass rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-yellow-300">{statusCounts.negotiating}</p>
          <p className="text-xs text-white/60">เจรจา</p>
        </div>
        <div className="glass rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-blue-300">{statusCounts.confirmed}</p>
          <p className="text-xs text-white/60">ยืนยัน</p>
        </div>
        <div className="glass rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-green-300">{statusCounts.completed}</p>
          <p className="text-xs text-white/60">สำเร็จ</p>
        </div>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass rounded-xl p-3 flex items-center gap-3"
      >
        <Search className="w-5 h-5 text-white/60" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="ค้นหาดีล..."
          className="flex-1 bg-transparent text-white placeholder-white/40 outline-none"
        />
      </motion.div>

      {/* Filter Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex items-center gap-2 overflow-x-auto pb-2"
      >
        {[
          { key: 'all', label: 'ทั้งหมด' },
          { key: 'negotiating', label: 'กำลังเจรจา' },
          { key: 'confirmed', label: 'ยืนยันแล้ว' },
          { key: 'held', label: 'เงินมัดจำ' },
          { key: 'completed', label: 'สำเร็จ' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key as typeof filter)}
            className={`px-4 py-2 rounded-xl whitespace-nowrap transition-all flex-shrink-0 ${
              filter === tab.key
                ? 'bg-teal-500/20 border border-teal-400/30 text-teal-300 shadow-[0_0_15px_rgba(20,184,166,0.3)]'
                : 'glass text-white/60 hover:bg-white/10'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </motion.div>

      {/* Deals List */}
      <div className="space-y-3">
        {filteredDeals.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass rounded-xl p-8 text-center"
          >
            <ShoppingBag className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60">ไม่พบดีลที่ตรงกับเงื่อนไข</p>
          </motion.div>
        ) : (
          filteredDeals.map((deal, index) => (
            <motion.div
              key={deal.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + index * 0.05 }}
              onClick={() => onDealClick(deal.id)}
              className="glass rounded-xl p-4 hover:bg-white/15 transition-all cursor-pointer active:scale-[0.98]"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3 flex-1">
                  {deal.item.imageUrl ? (
                    <img
                      src={deal.item.imageUrl}
                      alt={deal.item.name}
                      className="w-14 h-14 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <ShoppingBag className="w-7 h-7 text-teal-300" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-white truncate">{deal.item.name}</p>
                    <p className="text-sm text-white/60">{deal.item.category}</p>
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border mt-1 ${getStatusColor(deal.status)}`}
                    >
                      {getStatusText(deal.status)}
                    </span>
                  </div>
                </div>
                {deal.aiAssisted && (
                  <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-teal-500/20 flex-shrink-0 ml-2">
                    <Bot className="w-3 h-3 text-teal-300" />
                    <span className="text-xs text-teal-300">AI</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-white">
                    ฿{deal.price.current.toLocaleString()}
                  </p>
                  {deal.price.saved && deal.price.saved > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <TrendingUp className="w-3 h-3 text-green-400" />
                      <p className="text-sm text-green-400">
                        ประหยัด ฿{deal.price.saved.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/60">
                    {deal.type === 'buy' ? 'ซื้อ' : 'ขาย'}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3 text-white/40" />
                    <p className="text-xs text-white/40">
                      {new Date(deal.timeline.updated).toLocaleDateString('th-TH', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </p>
                  </div>
                </div>
              </div>

              {deal.status === 'negotiating' && (
                <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-teal-300" />
                    <span className="text-sm text-white/60">
                      {deal.counterparty.name}
                    </span>
                    <span className="text-sm text-teal-300">
                      Trust: {deal.counterparty.trustScore}
                    </span>
                  </div>
                  <button className="flex items-center gap-1 px-3 py-1 rounded-lg bg-teal-500/20 text-teal-300 hover:bg-teal-500/30 transition-colors active:scale-95">
                    <MessageSquare className="w-3 h-3" />
                    <span className="text-xs">เจรจา</span>
                  </button>
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}
